import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { AREA_HOME, useAccess } from "@/lib/access";
import { useAuth } from "@/context/AuthContext";
import AccessRestricted from "@/pages/AccessRestricted";
import { Receipt, ChartLineUp, SignOut, Package } from "@phosphor-icons/react";

export default function AreaPicker() {
  const { user, logout } = useAuth();
  const { loading, areas, hasAccess } = useAccess();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
        Loading workspaces…
      </div>
    );
  }
  if (!hasAccess) {
    return <AccessRestricted />;
  }
  // Single-area users auto-redirect
  if (areas.length === 1) {
    return <Navigate to={AREA_HOME[areas[0]] || "/invoices"} replace />;
  }
  // Multi-area user → show picker
  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 bg-slate-50"
      data-testid="area-picker"
    >
      <div className="max-w-3xl w-full">
        <div className="text-center mb-10">
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
            Choose a workspace
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Hi {user?.name?.split(" ")[0]}, you have access to multiple workspaces. Pick where you want to go.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {areas.includes("sales") && (
            <button
              type="button"
              onClick={() => navigate("/sales/dashboard")}
              className="text-left group bg-white border border-slate-200 hover:border-blue-300 hover:shadow-sm rounded-lg p-7 transition"
              data-testid="pick-sales"
            >
              <div className="w-12 h-12 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center mb-4 group-hover:bg-blue-100 transition">
                <ChartLineUp size={24} weight="duotone" />
              </div>
              <div className="font-heading text-xl font-bold text-slate-900">
                Sales
              </div>
              <div className="text-sm text-slate-500 mt-1">
                CRM, customers, pipeline, orders, vendors
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-blue-600 mt-4 font-semibold">
                /sales →
              </div>
            </button>
          )}
          {areas.includes("invoice") && (
            <button
              type="button"
              onClick={() => navigate("/invoices")}
              className="text-left group bg-white border border-slate-200 hover:border-rose-300 hover:shadow-sm rounded-lg p-7 transition"
              data-testid="pick-invoice"
            >
              <div className="w-12 h-12 rounded-md bg-rose-50 text-rose-700 flex items-center justify-center mb-4 group-hover:bg-rose-100 transition">
                <Receipt size={24} weight="duotone" />
              </div>
              <div className="font-heading text-xl font-bold text-slate-900">
                Finance
              </div>
              <div className="text-sm text-slate-500 mt-1">
                Invoices, AR follow-ups, products, settings
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-rose-600 mt-4 font-semibold">
                /invoices →
              </div>
            </button>
          )}
          {areas.includes("inventory") && (
            <button
              type="button"
              onClick={() => navigate("/inventory")}
              className="text-left group bg-white border border-slate-200 hover:border-emerald-300 hover:shadow-sm rounded-lg p-7 transition"
              data-testid="pick-inventory"
            >
              <div className="w-12 h-12 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center mb-4 group-hover:bg-emerald-100 transition">
                <Package size={24} weight="duotone" />
              </div>
              <div className="font-heading text-xl font-bold text-slate-900">
                Inventory
              </div>
              <div className="text-sm text-slate-500 mt-1">
                Items, stock, recipes, batch calculator, production
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-600 mt-4 font-semibold">
                /inventory →
              </div>
            </button>
          )}
        </div>
        <div className="mt-10 text-center">
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-700"
            data-testid="picker-signout"
          >
            <SignOut size={12} /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
